import { useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router';
import { useProducts } from '../api/products';
import type { TransactionRow } from '../api/reports';
import { useReportTransactions } from '../api/reports';
import { LastUpdated } from '../components/LastUpdated';
import { Money } from '../components/Money';
import type { SalesContext } from './Sales';
import { TransactionDrawer } from './TransactionDrawer';

// Stripe-side normalized brand strings. Source of truth is the Stripe API
// docs (PaymentMethodDetails.CardPresent.brand); listing the common ones is
// enough for operator search — unrecognized brands still display correctly,
// they just don't appear as a filter chip.
const CARD_BRANDS: Array<{ value: string; label: string }> = [
  { value: 'visa', label: 'Visa' },
  { value: 'mastercard', label: 'Mastercard' },
  { value: 'amex', label: 'Amex' },
  { value: 'discover', label: 'Discover' },
  { value: 'diners', label: 'Diners' },
  { value: 'jcb', label: 'JCB' },
  { value: 'unionpay', label: 'UnionPay' },
];

/** Debounce a value so rapid keystrokes don't fire a query per character. */
function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

const STATUS_PILL: Record<TransactionRow['status'], string> = {
  pending: 'pill-draft',
  succeeded: 'pill-paid',
  canceled: 'pill-declined',
  voided: 'pill-declined',
};

function tenderLabel(t: TransactionRow['tenderType']): string {
  switch (t) {
    case 'card_present':
      return 'Tap';
    case 'keyed':
      return 'Keyed';
    case 'cash':
      return 'Cash';
  }
}

export function SalesTransactions() {
  const { eventId, range } = useOutletContext<SalesContext>();
  const [status, setStatus] = useState<TransactionRow['status'] | undefined>(undefined);
  const [tender, setTender] = useState<TransactionRow['tenderType'] | undefined>(undefined);
  const [cardBrand, setCardBrand] = useState<string>('');
  const [last4Input, setLast4Input] = useState<string>('');
  const [productId, setProductId] = useState<string>('');
  const [open, setOpen] = useState<TransactionRow | null>(null);

  // Debounce last4 so we don't fire one query per typed digit. The input is
  // restricted to 4 digits; the query only fires when length is exactly 4 or
  // empty (intermediate 1-3 digit states are dropped).
  const last4Debounced = useDebounced(last4Input, 300);
  const last4ForQuery = last4Debounced.length === 4 ? last4Debounced : undefined;

  const products = useProducts(eventId);

  const {
    data,
    isLoading,
    isError,
    dataUpdatedAt,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useReportTransactions(eventId, {
    ...range,
    status,
    tender,
    cardBrand: cardBrand || undefined,
    cardLast4: last4ForQuery,
    productId: productId || undefined,
    limit: 50,
  });

  const rows = useMemo(
    () => data?.pages.flatMap((p) => p.rows) ?? [],
    [data?.pages],
  );

  // IntersectionObserver-driven infinite scroll. Sentinel sits below the table
  // and triggers fetchNextPage when it scrolls into view. rootMargin of 200px
  // pre-fetches the next page so the operator doesn't see a loading flash at
  // the bottom of the current page.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    if (!hasNextPage || isFetchingNextPage) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            void fetchNextPage();
            break;
          }
        }
      },
      { rootMargin: '200px' },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {/* Search row — composes with the chip filters below. */}
      <div
        style={{
          display: 'inline-flex',
          gap: 8,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <span style={{ color: 'var(--fg-3)', fontSize: 12 }}>Last 4</span>
          <input
            type="text"
            inputMode="numeric"
            pattern="\d{4}"
            maxLength={4}
            placeholder="••••"
            value={last4Input}
            onChange={(e) => setLast4Input(e.target.value.replace(/\D/g, ''))}
            style={{ width: 72 }}
            aria-label="Filter by card last 4 digits"
          />
        </label>
        <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <span style={{ color: 'var(--fg-3)', fontSize: 12 }}>Brand</span>
          <select
            value={cardBrand}
            onChange={(e) => setCardBrand(e.target.value)}
            aria-label="Filter by card brand"
          >
            <option value="">Any</option>
            {CARD_BRANDS.map((b) => (
              <option key={b.value} value={b.value}>
                {b.label}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <span style={{ color: 'var(--fg-3)', fontSize: 12 }}>Product</span>
          <select
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            aria-label="Filter by product"
            disabled={!products.data}
          >
            <option value="">Any</option>
            {products.data
              ?.filter((p) => p.archivedAt === null)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </select>
        </label>
        {(last4ForQuery || cardBrand || productId) && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              setLast4Input('');
              setCardBrand('');
              setProductId('');
            }}
          >
            Clear search
          </button>
        )}
      </div>

      <div style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap' }}>
        <FilterChip
          label="All"
          active={!status}
          onClick={() => setStatus(undefined)}
        />
        <FilterChip
          label="Succeeded"
          active={status === 'succeeded'}
          onClick={() => setStatus('succeeded')}
        />
        <FilterChip
          label="Pending"
          active={status === 'pending'}
          onClick={() => setStatus('pending')}
        />
        <FilterChip
          label="Canceled"
          active={status === 'canceled'}
          onClick={() => setStatus('canceled')}
        />
        <FilterChip
          label="Voided"
          active={status === 'voided'}
          onClick={() => setStatus('voided')}
        />
        <span style={{ width: 1, background: 'var(--border-1)' }} />
        <FilterChip
          label="Any tender"
          active={!tender}
          onClick={() => setTender(undefined)}
        />
        <FilterChip
          label="Tap-to-pay"
          active={tender === 'card_present'}
          onClick={() => setTender('card_present')}
        />
        <FilterChip
          label="Keyed"
          active={tender === 'keyed'}
          onClick={() => setTender('keyed')}
        />
        <FilterChip
          label="Cash"
          active={tender === 'cash'}
          onClick={() => setTender('cash')}
        />
      </div>

      {isLoading && <div style={{ color: 'var(--fg-3)' }}>Loading…</div>}
      {isError && <div className="form-error">Couldn't load transactions.</div>}
      {!isLoading && rows.length === 0 && (
        <div style={{ color: 'var(--fg-3)' }}>No transactions match these filters.</div>
      )}
      {rows.length > 0 && (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>When</th>
                <th>Status</th>
                <th>Tender</th>
                <th>Card</th>
                <th>Description</th>
                <th className="num">Amount</th>
                <th className="num">Net</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const ts = row.succeededAt ?? row.createdAt;
                return (
                  <tr
                    key={row.id}
                    onClick={() => setOpen(row)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="mono" style={{ whiteSpace: 'nowrap' }}>
                      {new Date(ts).toLocaleString()}
                    </td>
                    <td>
                      <span className={`pill ${STATUS_PILL[row.status]}`}>{row.status}</span>
                    </td>
                    <td>{tenderLabel(row.tenderType)}</td>
                    <td>
                      {row.cardBrand && row.cardLast4 ? (
                        <span style={{ color: 'var(--fg-3)' }}>
                          {row.cardBrand} ••{row.cardLast4}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.description ?? ''}
                    </td>
                    <td className="num">
                      <Money value={row.amountCents / 100} />
                    </td>
                    <td className="num">
                      <Money value={row.netCents / 100} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Infinite-scroll sentinel + footer status. The sentinel is always
          present so a re-fetch after a filter change can re-arm; the footer
          status text shows loading vs end-of-list. */}
      <div ref={sentinelRef} aria-hidden style={{ height: 1 }} />
      {rows.length > 0 && (
        <div style={{ color: 'var(--fg-3)', fontSize: 12, textAlign: 'center', padding: '8px 0' }}>
          {isFetchingNextPage
            ? 'Loading more…'
            : hasNextPage
              ? 'Scroll for more'
              : `${rows.length} transaction${rows.length === 1 ? '' : 's'} loaded`}
        </div>
      )}

      {open && (
        <TransactionDrawer eventId={eventId} txn={open} onClose={() => setOpen(null)} />
      )}
      <LastUpdated dataUpdatedAt={dataUpdatedAt} />
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="filter-chip"
      data-active={active}
      onClick={onClick}
    >
      {label}
    </button>
  );
}
